import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useListPayStationCashTransactions } from 'src/features/payment/api/useListPayStationCashTransactions';
import { badgeOptionsCashTransaction, payStationDeviceOptions } from 'src/features/payment/utils/constants';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import { PayStationCashTransaction } from 'src/features/payment/utils/types';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { useParking } from 'src/providers/ParkingProvider';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import React from 'react';
import { commonMessages } from 'src/utils/messages';

const columnHelper = createColumnHelper<PayStationCashTransaction>();

export default function CashTransactionsTable() {
    const { operationId = '' } = useParams();
    const { formatMessage, formatNumber } = useIntl();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySortingOptions, ...sorting } = useSorting({ initialSortingState: [{ id: 'created_at', desc: true }] });
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { queryFiltersOptions, ...filters } = useFilters(
        operationId ? { initialQueryFilters: { operation: operationId } } : undefined,
    );
    const { parkingId } = useParking();

    const { data, isError, isLoading } = useListPayStationCashTransactions({
        parkingId,
        ...queryPaginationOptions,
        ...querySearchOptions,
        ...querySortingOptions,
        ...queryFiltersOptions,
    });

    const [defaultDateFilter] = React.useState(() => {
        const now = Date.now();

        return {
            afterDate: new Date(now - 30 * 24 * 60 * 60 * 1000),
            beforeDate: new Date(),
        };
    });

    const columns = [
        columnHelper.accessor('created_at', {
            id: 'created_at',
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(paymentsMessages.createdAt),
            },
        }),
        columnHelper.accessor('device_type', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            enableColumnFilter: true,
            cell: (info) => {
                const value = info.getValue();
                if (typeof value !== 'number') return '-';
                return formatMessage(payStationDeviceOptions[value]);
            },
            meta: {
                title: formatMessage(paymentsMessages.deviceType),
                options: Object.entries(payStationDeviceOptions).map(([key, value]) => {
                    return { value: key, label: formatMessage(value) };
                }),
            },
        }),
        columnHelper.accessor('type', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(paymentsMessages.type),
                badgeOptions: badgeOptionsCashTransaction,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        columnHelper.accessor('denomination', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(paymentsMessages.nominalValue),
            },
        }),
        columnHelper.accessor('count', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(paymentsMessages.count),
            },
        }),
        columnHelper.accessor('amount', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({
                row: {
                    original: { amount, currency },
                },
            }) => {
                if (!amount) return '-';
                return formatNumber(amount / 100, {
                    style: 'currency',
                    currency,
                });
            },
            meta: {
                title: formatMessage(paymentsMessages.amount),
            },
        }),
        columnHelper.accessor('currency', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(paymentsMessages.currency),
            },
        }),
        columnHelper.accessor('created_at', {
            enableColumnFilter: true,
            meta: {
                title: formatMessage(commonMessages.time),
                inputType: INPUT_TYPE.DATE,
                dateFilter: defaultDateFilter,
            },
            id: 'created_at_range',
        }),
    ];

    return (
        <DataTable
            columns={columns as any}
            data={data}
            pagination={pagination}
            sorting={sorting}
            filters={filters}
            search={search}
            semantics={{
                title: formatMessage(paymentsMessages.cashTransactions),
                entryName: formatMessage(paymentsMessages.cashTransaction).toLowerCase(),
                entryNames: formatMessage(paymentsMessages.cashTransactions).toLowerCase(),
                initialColumnVisibility: {
                    created_at_range: false,
                },
            }}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
            }}
        />
    );
}
